import React from 'react'
import ReactTable from 'react-table'
import { saveAs } from 'file-saver'
import { HeaderCell } from '../RACTable/HeaderCell'
import { DateCell, dateFilter } from '../ResearchTables/DateCell'
import { RegionCell, regionFilter } from '../ResearchTables/RegionCell'
import { SectorCell, sectorFilter } from '../ResearchTables/SectorCell'
import { TitleCell, titleFilter } from '../ResearchTables/TitleCell'
import { DefaultFilter } from '../ResearchTables/RatingsCriteriaReportTable/RatingsCriteriaReportTable'
import { MarketingTag } from '../ResearchTables'
import { RatingOutlookCell, ratingOutlookFilter } from './RatingOutlookCell'
import { SectorOutlookCell, sectorOutlookFilter } from './SectorOutlookCell'
import { sortColumnValues } from '../utils/sortColumnValues'
import OutlookPopup from '../OutlookKey/OutlookPopup'
import {
  CommentaryOutlookCell,
  commentaryFilter
} from './CommentaryOutlookCell'
import { Button } from '../Button'
import ColumnFilter from '../ColumnFilter/ColumnFilter'
// Constants
const PAGE_ORIENTATION = 'portrait'
const DEFAULT_COL_WIDTH = 30
const DATA_SHEET_FIT_TO_PG = true
const DISCLAIMER_SHEET_FIT_TO_PG = true
// File / Sheet name defaults
const DEFAULT_FILE_NAME = `Outlooks_Data_Export.xlsx`
const DEFAULT_DATA_SHEET_TITLE = 'Outlooks Data Sheet'
const DEFAULT_DISCLAIMER_SHEET_TITLE = 'Disclaimer'

export type OutlookTableItemProps = {
  publishedDate: string
  regions?: MarketingTag[]
  sectors?: MarketingTag[]
  slug: string
  title: string
  sectorOutlook?: string
  sectorOutlookCode?: string
  ratingOutlook?: string
  ratingOutlookCode?: string
  taggedReports?: OutlookTaggedReport[]
}
export type ExcelRowCell = {
  value: any
  highlight?: boolean
}
export type ExcelSheetProps = {
  sheetTitle?: string
  data: ExcelRowCell[][]
  columns?: any
}

export type excelItemProps = {
  publishedDate: string
  regions?: any
  sectors?: any
  title: object
  sectorOutlook?: string
  ratingOutlook?: string
}
export type OutlookTaggedReport = {
  slug: string
  title: string
}

export type OutlookTableProps = {
  items: OutlookTableItemProps[]
  defaultFilters?: DefaultFilter[]
  isPrintPreview?: boolean
  excelItems: excelItemProps[]
  disclaimerTxt: string
  showSectorOutlooksColumn: boolean
  showRatingOutlooksColumn: boolean
}
interface RatablesState {
  page: number
  pageSize: number
  showPopup: boolean
  key: number
}
export const createDisclaimer = (
  workbook: any,
  disclaimerSheet: ExcelSheetProps
) => {
  const worksheet = workbook.addWorksheet(
    disclaimerSheet.sheetTitle || DEFAULT_DISCLAIMER_SHEET_TITLE,
    {
      pageSetup: { paperSize: 9, orientation: PAGE_ORIENTATION }
    }
  )
  const row = worksheet.getRow(2)
  const [disclaimerTxt] = disclaimerSheet.data
  // data rows
  if (row)
    row.values = disclaimerTxt.map((cell: ExcelRowCell) => cell.value) || ''
  // styles
  worksheet.mergeCells('A2:Q44')
  const cell = worksheet.getCell('A2')
  cell.alignment = { wrapText: true, vertical: 'top' }
  worksheet.getCell('A1').value = 'DISCLAIMER'
  worksheet.getRow(1).font = { bold: true }
  // print/page setup
  worksheet.pageSetup.fitToPage = DISCLAIMER_SHEET_FIT_TO_PG
}

export const createWorksheet = (
  workbook: any,
  excelItems: any,
  columnHeaders: any
) => {
  // worksheet data
  const worksheet = workbook.addWorksheet(DEFAULT_DATA_SHEET_TITLE, {
    pageSetup: { orientation: PAGE_ORIENTATION }
  })
  worksheet.properties.defaultColWidth = DEFAULT_COL_WIDTH
  worksheet.columns = columnHeaders
  const rows = excelItems
  // header
  const headerRow = worksheet.addRow(columnHeaders)
  const headerCell = headerRow
  if (headerCell) {
    headerCell.font = { bold: true }
    headerCell.alignment = {
      wrapText: true,
      vertical: 'top'
    }
  }
  const rowsData = rows.map((obj: { [x: string]: any }) =>
    Object.keys(obj).map((key) => obj[key])
  )
  // data rows
  worksheet.addRows(rowsData)
  // print/page setup
  worksheet.pageSetup.fitToPage = DATA_SHEET_FIT_TO_PG
}

const exportToCSV = async (excelItems: any, disclaimerTxt: any) => {
  const { Workbook } = await import('exceljs')
  const workbook = new Workbook()
  const columnHeaders = [
    'PUBLISH DATE',
    'FULL REPORT',
    'RELATED COMMENTARY',
    'SECTOR OUTLOOK',
    'RATING OUTLOOK',
    'SECTOR',
    'REGION'
  ]
  createWorksheet(workbook, excelItems, columnHeaders)
  const disclaimerSheet = {
    data: [[{ value: disclaimerTxt }]]
  }
  createDisclaimer(workbook, disclaimerSheet)
  workbook.xlsx.writeBuffer().then((buffer: BlobPart) => {
    saveAs(new Blob([buffer]), DEFAULT_FILE_NAME, { autoBom: false })
  })
}
export class OutlookTable extends React.Component<
  OutlookTableProps,
  RatablesState
> {
  state = {
    page: 0,
    pageSize: 10,
    showPopup: false,
    key: 0
  }

  taggedReports = {
    accessor: 'taggedReports',
    width: 210,
    Cell: (cellProps: any) => <CommentaryOutlookCell cellProps={cellProps} />,
    filterMethod: (filterItem: any, data: any) =>
      commentaryFilter(filterItem, data),
    Header: () => <HeaderCell heading="Related Commentary" />,
    id: 'commentary',
    Filter: ColumnFilter,
    sortMethod: (prev: OutlookTaggedReport[], next: OutlookTaggedReport[]) => {
      const previousTitle = prev.length ? prev[0].title : 'z'
      const nextTitle = next.length ? next[0].title : 'z'
      return sortColumnValues(previousTitle, nextTitle)
    }
  }

  publishedDate = {
    accessor: 'publishedDate',
    Cell: (cellProps: any) => <DateCell cellProps={cellProps} />,
    filterMethod: (filterItem: any, data: any) => dateFilter(filterItem, data),
    Header: () => <HeaderCell heading="Publish Date" />,
    id: 'publishedDate',
    Filter: ColumnFilter,
    sortMethod: (prev: any, next: any) =>
      new Date(prev).getTime() - new Date(next).getTime()
  }

  region = {
    accessor: 'regions',
    Cell: (cellProps: any) => <RegionCell cellProps={cellProps} />,
    filterMethod: (filterItem: any, data: any) =>
      regionFilter(filterItem, data),
    Header: () => <HeaderCell heading="Region" />,
    id: 'regions',
    Filter: ColumnFilter,
    sortMethod: (prev: MarketingTag[], next: MarketingTag[]) => {
      const prevRegion = prev[0].name
      const nextRegion = next[0].name
      return sortColumnValues(prevRegion, nextRegion)
    }
  }

  sector = {
    accessor: 'sectors',
    Cell: (cellProps: any) => <SectorCell cellProps={cellProps} />,
    filterMethod: (filterItem: any, data: any) =>
      sectorFilter(filterItem, data),
    Header: () => <HeaderCell heading="Sector" />,
    Filter: ColumnFilter,
    id: 'sectors',
    sortMethod: (prev: MarketingTag[], next: MarketingTag[]) => {
      const prevSector = prev[0].name
      const nextSector = next[0].name
      return sortColumnValues(prevSector, nextSector)
    }
  }

  title = {
    accessor: 'title',
    Cell: (cellProps: any) => <TitleCell cellProps={cellProps} />,
    filterMethod: (filterItem: any, data: any) => titleFilter(filterItem, data),
    Header: () => <HeaderCell heading="Full Report" />,
    id: 'title',
    Filter: ColumnFilter,
    sortMethod: (prev: string, next: string) => sortColumnValues(prev, next)
  }

  sectorOutlook = (
    isPrintPreview: boolean,
    showSectorOutlooksColumn = true
  ) => ({
    accessor: 'sectorOutlook',
    width: 175,
    show: showSectorOutlooksColumn,
    Cell: (cellProps: any) => (
      <SectorOutlookCell
        cellProps={cellProps}
        isPrintPreview={isPrintPreview}
        handleClick={this.cellClick}
      />
    ),
    filterMethod: (filterItem: any, data: any) =>
      sectorOutlookFilter(filterItem, data),
    Filter: ColumnFilter,
    Header: () => <HeaderCell heading="Sector Outlook" />,
    id: 'sectorOutlook',
    sortMethod: (prev: string, next: string) => sortColumnValues(prev, next)
  })

  ratingOutlook = (
    isPrintPreview: boolean,
    showRatingOutlooksColumn = true
  ) => ({
    accessor: 'ratingOutlook',
    width: 175,
    show: showRatingOutlooksColumn,
    Cell: (cellProps: any) => (
      <RatingOutlookCell
        cellProps={cellProps}
        isPrintPreview={isPrintPreview}
        handleClick={this.cellClick}
      />
    ),
    filterMethod: (filterItem: any, data: any) =>
      ratingOutlookFilter(filterItem, data),
    Filter: ColumnFilter,
    Header: () => <HeaderCell heading="Rating Outlook" />,
    id: 'ratingOutlook',
    sortMethod: (prev: string, next: string) => sortColumnValues(prev, next)
  })

  createColumns = (props: OutlookTableProps) => {
    /* If year column is required later add year cell related code , refer to below RWR PRs
      Storybook- #736
      React -#812
    */
    const { isPrintPreview = false } = props
    const { showSectorOutlooksColumn, showRatingOutlooksColumn } = props
    return [
      this.publishedDate,
      this.title,
      this.taggedReports,
      this.sectorOutlook(isPrintPreview, showSectorOutlooksColumn),
      this.ratingOutlook(isPrintPreview, showRatingOutlooksColumn),
      this.sector,
      this.region
    ]
  }

  cellClick = () => {
    const { showPopup } = this.state
    this.setState({ showPopup: !showPopup })
  }

  render() {
    const { items, defaultFilters, excelItems, disclaimerTxt } = this.props
    const columns = this.createColumns(this.props)

    const { page, pageSize, showPopup, key } = this.state
    return (
      <div>
        <div style={{ marginLeft: '25px' }}>
          <Button
            handleClick={() => exportToCSV(excelItems, disclaimerTxt)}
            className="frw-search__tools-title"
            title="Excel Export"
          />
          <i style={{ marginLeft: '.5rem' }} className="far fa-file-excel" />
        </div>
        <ReactTable
          columns={columns}
          data={items}
          defaultFiltered={defaultFilters}
          defaultPageSize={10}
          filterable
          minRows={0}
          page={page}
          key={key}
          pageSize={pageSize}
          onPageChange={(pageIndex: number) => {
            this.setState({
              page: pageIndex
            })
          }}
          onPageSizeChange={(newPageSize: number) => {
            this.setState((prevState) => {
              const { page, pageSize, key } = prevState
              const newPage = Math.floor((page * pageSize) / newPageSize)
              return {
                page: newPage,
                pageSize: newPageSize,
                key: key + 1
              }
            })
          }}
          getTdProps={() => {
            return {
              tabIndex: 0
            }
          }}
        />
        {showPopup && <OutlookPopup handleClick={this.cellClick} />}
      </div>
    )
  }
}
